/**
 * Currency Input Component
 * Formats input as currency ($XXX.XX)
 * Matches Angular's appAmount directive behavior
 */

import { useState, useEffect, useRef } from 'react'
import { cn } from '@lib/utils/helpers'

/** Standard max digits for income (before decimal) to avoid overflow – e.g. 15 = up to 999,999,999,999,999.99 */
const DEFAULT_MAX_DIGITS_BEFORE_DECIMAL = 15

interface CurrencyInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string
  onChange: (value: string) => void
  error?: string
  /** Max digits before decimal (default 15). Prevents >20 digit entry that causes errors. */
  maxDigitsBeforeDecimal?: number
}

export function CurrencyInput({
  value,
  onChange,
  error,
  className,
  onBlur,
  maxDigitsBeforeDecimal = DEFAULT_MAX_DIGITS_BEFORE_DECIMAL,
  ...props
}: CurrencyInputProps) {
  const [displayValue, setDisplayValue] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Format value for display
  useEffect(() => {
    if (!isFocused) {
      // Format when not focused (on blur)
      if (value) {
        const cleaned = value.replace(/[^0-9.]/g, '')
        if (cleaned) {
          const num = parseFloat(cleaned)
          if (!isNaN(num)) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: sync the formatted display to the value prop when the input is not focused
            setDisplayValue(`$ ${num.toFixed(2)}`)
          } else {
            setDisplayValue('')
          }
        } else {
          setDisplayValue('')
        }
      } else {
        setDisplayValue('')
      }
    } else {
      // Show raw value while typing (with $ prefix for consistency)
      if (value) {
        const cleaned = value.replace(/[^0-9.]/g, '')
        if (cleaned) {
          // Show as user types: $123.45 (without forcing .00)
          const parts = cleaned.split('.')
          if (parts.length === 2) {
            setDisplayValue(`$ ${parts[0]}.${parts[1].slice(0, 2)}`)
          } else {
            setDisplayValue(`$ ${cleaned}`)
          }
        } else {
          setDisplayValue('')
        }
      } else {
        setDisplayValue('')
      }
    }
  }, [value, isFocused])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    
    // Remove currency symbol and format characters, keep only numbers and decimal
    let cleaned = inputValue.replace(/[^0-9.]/g, '')
    
    // Ensure only one decimal point
    const parts = cleaned.split('.')
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('')
    }
    
    // Limit to 2 decimal places
    if (parts.length === 2 && parts[1].length > 2) {
      cleaned = parts[0] + '.' + parts[1].slice(0, 2)
    }
    // Limit digits before decimal (standard for income – avoids >20 digit error)
    const [intPart, decPart] = cleaned.split('.')
    if ((intPart || '').length > maxDigitsBeforeDecimal) {
      cleaned = (intPart || '').slice(0, maxDigitsBeforeDecimal) + (decPart != null ? '.' + decPart.slice(0, 2) : '')
    }
    // Call onChange with cleaned numeric value (not formatted)
    onChange(cleaned)
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    if (props.onFocus) {
      props.onFocus(e)
    }
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    // Format on blur
    if (value) {
      const cleaned = value.replace(/[^0-9.]/g, '')
      if (cleaned) {
        const num = parseFloat(cleaned)
        if (!isNaN(num)) {
          setDisplayValue(`$ ${num.toFixed(2)}`)
        }
      }
    }
    if (onBlur) {
      onBlur(e)
    }
  }

  return (
    <>
      <input
        {...props}
        ref={inputRef}
        type="text"
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn(
          'h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 shadow-sm transition-all',
          'focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/25 focus:border-[rgb(21_151_255)]',
          'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:opacity-70',
          error
            ? '!border-red-500 !border-solid focus:!border-red-500 focus:!ring-2 focus:!ring-red-500'
            : '',
          className,
        )}
      />
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </>
  )
}
